import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import OrgSwitcher from '@/components/layout/OrgSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';

/** At most one org refresh per this window, on window focus only. */
const FOCUS_REFRESH_MS = 30_000;

/**
 * The waiting page (BLU-1376, handover 4.5): a user of an org in setup mode
 * who cannot read setup (no `org_settings.read`: Viewer, Warehouse Staff, CX,
 * stations) lands on /onboarding and sees this instead of the wizard. It
 * offers the org switcher and Sign out, and refreshes the org on window focus
 * (throttled), so it leaves on its own once the admin finishes setup. Light
 * only (D15).
 */
export function SetupWaitingPage() {
  const { t } = useTranslation('onboarding');
  const { signOut } = useAuth();
  const { reloadOrganization } = useOrganization();
  const lastRefresh = useRef(0);

  useEffect(() => {
    const onFocus = () => {
      const now = Date.now();
      if (now - lastRefresh.current < FOCUS_REFRESH_MS) return;
      lastRefresh.current = now;
      reloadOrganization();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reloadOrganization]);

  return (
    <div className="mx-auto mt-16 max-w-md rounded-xl border bg-card p-8 text-center shadow-sm">
      <h1 className="text-lg font-semibold tracking-tight">{t('setupMode.waiting.title')}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t('setupMode.waiting.body')}</p>
      <div className="mt-6 space-y-2 text-left">
        <p className="text-xs text-muted-foreground">{t('setupMode.waiting.switchOrg')}</p>
        <OrgSwitcher />
      </div>
      <Button variant="outline" className="mt-6" onClick={() => void signOut()}>
        {t('setupMode.waiting.signOut')}
      </Button>
    </div>
  );
}
